import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {classes, style, vars} from './multiline-expandable-text.st.css';
import {useElementHeightBinding, useElementSizeBinding, useRenderState} from "../hooks";
import {
    arrayFindIndexFromEnd,
    arrayFindValueFromEnd, arrayFirst,
    arrayFlatten,
    arrayLast,
    arrayRemoveUndefinedValues,
    arraySkipTake
} from "@wix/devzai-utils-common";
import {Button} from "../button/button";
import {domNodesListToArray, elementHtmlCollectionToArray} from "@wix/devzai-utils-dom";
import {DynamicRef} from "../dynamic-ref";

export const MultilineExpandableText = React.memo(function MultilineExpandableText (props: MultilineExpandableText.Props) {
    const {
        text,
        expandButtonText,
        maxLines = 2,
        lineHeight,
        collapseOnTextChange = true,

        style: styleProp,
        className,
        ...htmlAttributes
    } = props;

    const [isExpanded, setIsExpanded] = useState(false);

    const contentWrapperRef = useRef<HTMLDivElement>(null);

    const [testElement, setTestElement] = useState<HTMLDivElement | null>(null)
    const testElementRef = useMemo(() => {
        return DynamicRef.create<HTMLDivElement>({
            changeCallback: (element) => {
                setTestElement(element)
            }
        })
    }, []);

    const {
        width: testElementWidth,
        height: testElementHeight
    } = useElementSizeBinding(testElementRef, !isExpanded) ?? {width: undefined, height: undefined};
    const contentWrapperHeight = useElementHeightBinding(contentWrapperRef, !isExpanded) ?? undefined;

    const [testElementText, setTestElementText] = useState<string>();

    const renderState = useRenderState({
        collapseOnTextChange: collapseOnTextChange
    })

    useEffect(() => {
        if (renderState.current.collapseOnTextChange) {
            setIsExpanded(false);
        }
    }, [text, renderState]);

    useLayoutEffect(() => {

        if (!testElement) {
            return undefined;
        }

        const observer = new MutationObserver(() => {
            setTestElementText(testElement.textContent ?? '');
        });

        setTestElementText(testElement.textContent ?? '');

        observer.observe(testElement, {childList: true, subtree: true, characterData: true});

        return () => {
            observer.disconnect();
        };
    }, [testElement]);

    const truncatedText = useMemo(() => {

        if (isExpanded) {
            return null;
        }

        const contentWrapper = contentWrapperRef.current;
        if (testElementText && testElement && contentWrapper && testElementHeight !== undefined && contentWrapperHeight !== undefined) {

            const testElementChildren = elementHtmlCollectionToArray<HTMLElement>(testElement.children);
            const expandButton = testElementChildren.pop();
            const lastChild = arrayLast(testElementChildren);

            if (expandButton && lastChild && lastChild.offsetTop + lastChild.offsetHeight > testElementHeight) {

                // console.count('testElement')

                const expandButtonWidth = expandButton.offsetWidth;

                const isVisibleTextElement = (textElement: HTMLElement) => {
                    return (
                        textElement.offsetTop < testElementHeight &&
                        textElement.offsetLeft + textElement.offsetWidth + expandButtonWidth < testElementWidth
                    )
                }

                const lastVisibleWord = arrayFindValueFromEnd(testElementChildren, isVisibleTextElement);

                if (lastVisibleWord) {

                    const textElementChildNodes = domNodesListToArray(testElement.childNodes);
                    const visibleWordNodeIndex = textElementChildNodes.indexOf(lastVisibleWord);

                    if (visibleWordNodeIndex >= 0) {
                        return arraySkipTake(textElementChildNodes, 0, visibleWordNodeIndex + 1)
                            .map(node => node.textContent).join('');
                    } else {
                        return '';
                    }

                } else {

                    const firstChild = arrayFirst(testElementChildren);

                    if (firstChild) {
                        const firstChildChildren =
                            elementHtmlCollectionToArray<HTMLElement>(firstChild.children);

                        const lastVisibleFirstWordLetterIndex =
                            arrayFindIndexFromEnd(firstChildChildren, isVisibleTextElement);

                        if (lastVisibleFirstWordLetterIndex >= 0) {
                            const truncatedFirstWord = firstChild.textContent?.substring(0, lastVisibleFirstWordLetterIndex + 1)

                            const textElementChildNodes = domNodesListToArray(testElement.childNodes);
                            const firstWordNodeIndex = textElementChildNodes.indexOf(firstChild);

                            if (firstWordNodeIndex >= 0) {
                                return arraySkipTake(textElementChildNodes, 0, firstWordNodeIndex)
                                    .map(node => node.textContent).join('') + truncatedFirstWord;
                            } else {
                                return '';
                            }
                        } else {
                            return '';
                        }
                    } else {
                        return '';
                    }
                }
            }

        }

        return null;

    }, [testElementHeight, testElement, testElementText, contentWrapperHeight, testElementWidth, isExpanded])

    const testElementContent = useMemo(() => {
        const lines = text.split('\n');
        let isFirstWord = true;

        return lines.map((line, lineIndex) => {
            const words = line.split(' ');

            return arrayRemoveUndefinedValues([
                ...arrayFlatten(words.map((word, wordIndex) => {

                    const isWord = word !== '';

                    const result = [
                        isWord ? (
                            <span key={`${lineIndex}-${wordIndex}`}>
                                {
                                    isFirstWord ?
                                        word.split('').map((letter, letterIndex) => (
                                            <span key={letterIndex}>{letter}</span>
                                        )) :
                                        word
                                }
                            </span>
                        ) : undefined,
                        wordIndex < words.length - 1 ? ` ` : undefined
                    ];

                    if (isWord) {
                        isFirstWord = false;
                    }

                    return result
                })),
                lineIndex < lines.length - 1 ? `\n` : undefined
            ])
        })
    }, [text])

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {isExpanded: isExpanded}, className)}
            style={{
                ...styleProp,
                [vars.maxLines]: maxLines,
                [vars.lineHeight]: lineHeight,
            }}
        >
            <div
                ref={contentWrapperRef}
                className={style(classes.contentWrapper, {})}
            >
                <div
                    className={style(classes.testTextContainer)}
                    ref={testElementRef}
                >
                    {testElementContent}
                    <span>
                        <span>{`...`}</span>
                        <Button
                            className={style(classes.expandButton, {})}
                        >
                            {expandButtonText}
                        </Button>
                    </span>
                </div>
                <div
                    className={style(classes.textContainer, {})}
                >
                    {truncatedText !== null && !isExpanded ? (
                        <>
                            {truncatedText}
                            <span>{`...`}</span>
                            <Button
                                className={style(classes.expandButton, {})}
                                onClick={() => {
                                    setIsExpanded(true);
                                }}
                            >
                                {expandButtonText}
                            </Button>
                        </>
                    ) : text}
                </div>
            </div>
        </div>
    );
});

export namespace MultilineExpandableText {

    export interface Props extends React.HTMLAttributes<any> {
        children?: undefined;
        text: string;
        expandButtonText: string;
        maxLines?: number;
        lineHeight?: string;
        collapseOnTextChange?: boolean;
    }
}